import React, { useState } from 'react';
import { Button } from '@common/components';
import { useMutation } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import { PerformWorksShiftAction } from '@scheduling/graphql';
import { ShiftRejectModal } from '@scheduling/components';

import { performWorksShiftActionErrorMessage } from './_perform-works-shift-action-button-service';

// The Approve mutation re-fetches these on BOTH success and failure. On a failed Approve
// (the Phase-2 SHIFT_NOT_APPROVABLE signal) this is the cache self-heal for WORK-6575: the
// stale drawer row loses its Approve/Reject buttons and falls back to its state badge with
// no manual refresh. It works ONLY because the app's global mutate errorPolicy is 'all'
// (works/legacy/lib/graph-ql.js) — under 'all' a top-level GraphQL error RESOLVES the
// mutation, so Apollo still dispatches refetchQueries. The drag-drop hook
// (_schedule-review-page-drag-drop-hook.ts) pins errorPolicy:'none' for the exact opposite
// reason — do not "fix" this by switching to 'none' or the self-heal silently dies.
// (Guarded by perform-works-shift-action-button.self-heal.spec.tsx.)
export const SELF_HEAL_REFETCH_QUERIES = [
  'ShiftGapDetails',
  'PaginatedShiftGapOpenings',
  'ShiftsByDay',
];

export function PerformWorksShiftActionButton({
  worksShiftId,
  type,
}: {
  worksShiftId: string;
  type: 'confirm' | 'reject';
}) {
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectShiftId, setRejectShiftId] = useState('');

  const [performConfirmAction, { loading }] = useMutation(
    PerformWorksShiftAction,
    {
      onCompleted: (response) => {
        const worksShift = response.performWorksShiftAction?.worksShift;

        const integrationEnabled =
          worksShift?.organization?.vendorShiftBookingEnabled;
        let message =
          'Shift confirmed. Please be sure this shift is booked to your schedule.';
        if (integrationEnabled) {
          message =
            'Shift approved. This shift will be booked to your schedule shortly.';
        }
        createMessage('success', message);
      },
      onError: (error) => {
        const { kind, message } = performWorksShiftActionErrorMessage(error);
        createMessage(kind, message);
      },
      refetchQueries: SELF_HEAL_REFETCH_QUERIES,
    }
  );

  const confirmWorksShiftState = () => {
    performConfirmAction({
      variables: {
        worksShiftId: worksShiftId,
        input: {
          action: 'confirm',
        },
      },
    });
  };

  return (
    <>
      {type === 'confirm' ? (
        <Button
          size="md"
          kind="secondary"
          loading={loading}
          disabled={loading}
          dataTestId="perform-works-shift-approve-button"
          onClick={(e) => {
            e.stopPropagation();
            confirmWorksShiftState();
          }}
        >
          Approve
        </Button>
      ) : (
        <div onClick={(e) => e.stopPropagation()}>
          <Button
            size="md"
            kind="secondary"
            danger
            onClick={() => {
              setRejectModalOpen(true);
              setRejectShiftId(worksShiftId);
            }}
          >
            Reject
          </Button>
          <ShiftRejectModal
            open={rejectModalOpen}
            worksShiftId={rejectShiftId}
            setOpen={(value) => setRejectModalOpen(value)}
          />
        </div>
      )}
    </>
  );
}
